/* eslint-disable no-console */
import { useCallback, useEffect, useState } from "react";
import { queryClient } from "$utils/queryClient";
import { getProductByReference } from "$services/Products.service";
import ReferenceType from "$types/ReferenceType";
import { CacheInvalidationAction } from "$utils/cache";
import { normalizeArray } from "$utils/utils";

export const useDefaultValueReferencing = ({ companyId, reception }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [defaultValues, setDefaultValue] = useState<any>({
    orderReferences: [],
    productReferenceLists: [],
    receptionReferences: [],
    receptionProduct: {},
  });

  const fetchProductList = useCallback(
    async (wantedList) => {
      setIsLoading(true);
      const plist = await Promise.all(
        wantedList
          .map((r) => {
            let data;
            data = queryClient.getQueryData([
              CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE,
              companyId,
              r.productReference,
            ]) as any;
            if (!data) {
              data = queryClient.fetchQuery({
                queryKey: [
                  CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE,
                  companyId,
                  r.productReference,
                ],

                queryFn: () =>
                  getProductByReference({
                    company: companyId,
                    reference: r.productReference,
                  }),

                gcTime: 5 * 60 * 1000,
              });
            }

            return data;
          })
          .flat()
      );
      setIsLoading(false);
      return plist.flat();
    },
    [companyId]
  );

  /**
   * Split receptionReferences into orderReferences & productReferences
   */
  const splitReceptionReferences = useCallback(
    async (receptionReferences) => {
      const getSuborders = (receptionReference, suborderReferences) => {
        const allSuborders = normalizeArray(receptionReference.orders).flatMap(
          (o) => o.suborders
        );
        return allSuborders.filter((suborder) =>
          suborderReferences.includes(suborder?.reference)
        );
      };

      const productList = await fetchProductList(receptionReferences);

      const orderReferences = receptionReferences
        .filter((rr) => rr.referenceType === ReferenceType.ORDER)
        .map((receptionReference) => {
          const { productReference, suborderReferences, quantity } =
            receptionReference;
          const product = productList
            ?.flat()
            .find(
              (p) => p.reference === productReference && p.company === companyId
            );
          const allSuborders = getSuborders(
            receptionReference,
            suborderReferences
          );
          const productQty = product?.quantity;
          const rrQty = quantity;
          // Prefer order DB quantity when > 0; if 0 or missing, show product quantity (fallback).
          const resolvedQuantity =
            typeof rrQty === "number" && rrQty > 0
              ? rrQty
              : productQty !== undefined && productQty !== null
                ? productQty
                : typeof rrQty === "number"
                  ? rrQty
                  : (productQty ?? 1);

          return {
            ...receptionReference,
            receptionProduct: {
              categories: product?.categories,
              description: product?.description,
              designation: product?.designation,
              quantity: resolvedQuantity,
              // Order referencing: when product is not found in catalog, still display the original reference
              reference: product?.reference ?? productReference,
              unit: product?.unit,
            },
            reference: receptionReference.productReference,
            suborders: allSuborders,
            unit: product?.unit,
          };
        });
      const productReferenceLists = [];
      receptionReferences
        .filter((rr) => rr.referenceType === ReferenceType.PRODUCT)
        .forEach((receptionReference) => {
          const { suborderReferences } = receptionReference;
          const foundList = productReferenceLists.find(
            (prl) =>
              JSON.stringify(prl.suborders.map((s) => s.reference).sort()) ===
              JSON.stringify(suborderReferences.sort())
          );
          const suborders = getSuborders(
            receptionReference,
            suborderReferences
          );

          // Add safety check for suborders
          if (!suborders || suborders.length === 0) {
            console.warn(
              "No suborders found for reception reference:",
              receptionReference
            );
            return; // Skip this iteration if no suborders
          }

          if (foundList) {
            foundList.productReferences.push({
              ...receptionReference,
              suborders,
            });
          } else {
            productReferenceLists.push({
              id: suborders[0].id,
              suborders,
              productReferences: [
                {
                  ...receptionReference,
                  suborders,
                },
              ],
            });
          }
        });

      const completeProductReferenceLists = productReferenceLists.map(
        (productReferenceList) => {
          const { productReferences, suborders } = productReferenceList;

          if (!suborders || suborders.length === 0) {
            console.warn(
              "No suborders in productReferenceList:",
              productReferenceList
            );
            return {
              id: null,
              suborders: [],
              productReferences: productReferences || [],
            };
          }

          const newProductReferences = [...productReferences];
          const productRefs = productReferences.map(
            (pr) => pr.originalProductReference
          );
          suborders.forEach((suborder) => {
            if (!suborder || !suborder.suborderProducts) {
              console.warn("Invalid suborder:", suborder);
              return;
            }

            suborder.suborderProducts.forEach((product) => {
              if (!productRefs.includes(product.reference)) {
                const completeProduct = productList
                  ?.flat()
                  .find(
                    (p) =>
                      p.reference === product.reference &&
                      p.company === companyId
                  );
                if (completeProduct) {
                  newProductReferences.push({
                    ...completeProduct,
                    suborders,
                    originalProductReference: completeProduct.reference,
                  });
                } else {
                  console.error(`Product not found: ${product.reference}`);
                }
              }
            });
          });
          return {
            id: suborders[0].id,
            suborders,
            productReferences: newProductReferences.sort((p1, p2) =>
              (p1.originalProductReference ?? "").localeCompare(
                p2.originalProductReference ?? ""
              )
            ),
          };
        }
      );
      return {
        orderReferences,
        productReferenceLists: completeProductReferenceLists,
      };
    },
    [fetchProductList, companyId]
  );

  const getValues = useCallback(async () => {
    const { orderReferences, productReferenceLists } =
      await splitReceptionReferences(reception.receptionReferences);

    setDefaultValue({
      receptionReferences: reception.receptionReferences,
      orderReferences,
      productReferenceLists,
    });
  }, [reception.receptionReferences, splitReceptionReferences]);

  useEffect(() => {
    getValues();
  }, [getValues]);

  return { defaultValues, isLoading };
};
